import { Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { BusinessCenterOSAdminShell } from "../../components/layout/BusinessCenterOSAdminShell";
import { getAdminClients, getBusinessCenterProjects } from "../../lib/api";
import { formatDate } from "../../lib/format";
import type { BusinessCenterDocumentRecord, BusinessCenterProjectRecord, ClientRecord } from "../../lib/types";
import {
  createBusinessCenterDocument,
  documentFolderKindOptions,
  documentStatusOptions,
  documentTypeOptions,
  loadBusinessCenterDocumentsForBrand,
  refreshBusinessCenterDocuments,
  removeBusinessCenterDocument,
  updateBusinessCenterDocument,
  upsertBusinessCenterDocument,
} from "./businessCenterDocumentsData";

type BrandValue = "kreatethedate" | "truevance_connect";

const brandOptions: Array<{ value: BrandValue; label: string; accent: string }> = [
  { value: "truevance_connect", label: "Truevance Connect", accent: "#06B6D4" },
  { value: "kreatethedate", label: "Kreatethedate", accent: "#FF6B35" },
];

interface DocumentFormState {
  brand: BrandValue;
  folderKind: BusinessCenterDocumentRecord["folderKind"];
  documentName: string;
  documentType: BusinessCenterDocumentRecord["documentType"];
  status: BusinessCenterDocumentRecord["status"];
  clientId: string;
  projectId: string;
  notes: string;
}

const blankForm: DocumentFormState = {
  brand: "truevance_connect",
  folderKind: "Client",
  documentName: "",
  documentType: "Proposal",
  status: "Draft",
  clientId: "",
  projectId: "",
  notes: "",
};

export function BusinessCenterOSDocumentCenterPage() {
  const [selectedBrand, setSelectedBrand] = useState<BrandValue>("truevance_connect");
  const [documents, setDocuments] = useState<BusinessCenterDocumentRecord[]>([]);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [projects, setProjects] = useState<BusinessCenterProjectRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingWorkspace, setLoadingWorkspace] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(true);
  const [selectedDocumentId, setSelectedDocumentId] = useState("");
  const [form, setForm] = useState<DocumentFormState>(blankForm);

  useBusinessCenterOSBranding(
    "Document Center",
    "Brand-scoped document center for client folders, project folders, and placeholder upload records.",
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadingWorkspace(true);
    setError("");
    refreshBusinessCenterDocuments()
      .then(() => {
        if (!cancelled) setDocuments(loadBusinessCenterDocumentsForBrand(selectedBrand));
      })
      .catch(() => {
        if (!cancelled) setDocuments(loadBusinessCenterDocumentsForBrand(selectedBrand));
      });

    void Promise.all([getAdminClients({ brand: selectedBrand }), getBusinessCenterProjects({ brand: selectedBrand })])
      .then(([brandClients, brandProjects]) => {
        if (cancelled) return;
        setClients(brandClients);
        setProjects(brandProjects.projects || []);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setClients([]);
          setProjects([]);
          setError(
            loadError instanceof Error && loadError.message.includes("Failed")
              ? loadError.message
              : "Document workspace is using local fallback data because backend client/project lookup is unavailable.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setLoadingWorkspace(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedBrand]);

  const filteredDocuments = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return documents.filter((document) =>
      [
        document.documentNumber,
        document.documentName,
        document.documentType,
        document.folderKind,
        document.clientName,
        document.projectName,
        document.status,
        document.notes,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery),
    );
  }, [documents, query]);

  const metrics = useMemo(() => {
    const folders = {
      clientFolders: new Set(filteredDocuments.filter((document) => document.folderKind === "Client").map((document) => document.clientName || "Unlinked Client")).size,
      projectFolders: new Set(filteredDocuments.filter((document) => document.folderKind === "Project").map((document) => document.projectName || "Unlinked Project")).size,
      sharedFolders: filteredDocuments.filter((document) => document.folderKind === "Shared").length,
    };
    const counts = documentStatusOptions.reduce<Record<string, number>>((acc, status) => {
      acc[status] = filteredDocuments.filter((document) => document.status === status).length;
      return acc;
    }, {});
    return [
      { label: "Documents", value: filteredDocuments.length, note: "Brand-scoped document records in this workspace." },
      { label: "Client folders", value: folders.clientFolders, note: "Folder placeholders grouped by client." },
      { label: "Project folders", value: folders.projectFolders, note: "Folder placeholders grouped by project." },
      { label: "Uploaded", value: counts.Uploaded || 0, note: "Documents already marked as uploaded." },
      { label: "In Review", value: counts["In Review"] || 0, note: "Documents waiting for review." },
      { label: "Approved", value: counts.Approved || 0, note: "Documents ready for use." },
      { label: "Archived", value: counts.Archived || 0, note: "Documents kept for history." },
    ];
  }, [filteredDocuments]);

  const selectedDocument = documents.find((document) => document.id === selectedDocumentId) || filteredDocuments[0] || documents[0] || null;

  useEffect(() => {
    if (!selectedDocumentId && selectedDocument) {
      setSelectedDocumentId(selectedDocument.id);
    }
  }, [selectedDocument, selectedDocumentId]);

  useEffect(() => {
    if (selectedDocumentId && !documents.some((document) => document.id === selectedDocumentId)) {
      setSelectedDocumentId(filteredDocuments[0]?.id || documents[0]?.id || "");
    }
  }, [documents, filteredDocuments, selectedDocumentId]);

  function updateForm<K extends keyof DocumentFormState>(key: K, value: DocumentFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function resetForm(nextBrand: BrandValue = selectedBrand) {
    setForm({ ...blankForm, brand: nextBrand });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const client = clients.find((item) => item.id === form.clientId);
      const project = projects.find((item) => item.id === form.projectId);
      const nextFolderKind = form.folderKind || (project ? "Project" : client ? "Client" : "Shared");
      const nextDocument = createBusinessCenterDocument({
        brand: form.brand,
        folderKind: nextFolderKind,
        clientId: client?.id || null,
        clientName: client?.clientName || "Unlinked Client",
        projectId: project?.id || null,
        projectName: project?.projectName || "Unlinked Project",
        documentName: form.documentName,
        documentType: form.documentType,
        status: form.status,
        notes: form.notes,
      });
      const persisted = upsertBusinessCenterDocument(nextDocument);
      setDocuments(persisted.filter((document) => document.brand === selectedBrand));
      setSelectedDocumentId(nextDocument.id);
      resetForm(form.brand);
      setShowCreateForm(false);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to create document record.");
    } finally {
      setSaving(false);
    }
  }

  function handleDocumentStatusChange(documentId: string, nextStatus: BusinessCenterDocumentRecord["status"]) {
    const nextDocuments = updateBusinessCenterDocument(documentId, { status: nextStatus });
    setDocuments(nextDocuments.filter((document) => document.brand === selectedBrand));
  }

  function handleDeleteDocument(documentId: string) {
    const nextDocuments = removeBusinessCenterDocument(documentId);
    setDocuments(nextDocuments.filter((document) => document.brand === selectedBrand));
    setSelectedDocumentId(nextDocuments.find((document) => document.brand === selectedBrand)?.id || "");
  }

  const groupedClientFolders = useMemo(() => {
    const map = new Map<string, BusinessCenterDocumentRecord[]>();
    filteredDocuments
      .filter((document) => document.folderKind === "Client")
      .forEach((document) => {
        const key = document.clientName || "Unlinked Client";
        map.set(key, [...(map.get(key) || []), document]);
      });
    return Array.from(map.entries())
      .map(([name, docs]) => ({ name, docs }))
      .sort((a, b) => b.docs.length - a.docs.length);
  }, [filteredDocuments]);

  const groupedProjectFolders = useMemo(() => {
    const map = new Map<string, BusinessCenterDocumentRecord[]>();
    filteredDocuments
      .filter((document) => document.folderKind === "Project")
      .forEach((document) => {
        const key = document.projectName || "Unlinked Project";
        map.set(key, [...(map.get(key) || []), document]);
      });
    return Array.from(map.entries())
      .map(([name, docs]) => ({ name, docs }))
      .sort((a, b) => b.docs.length - a.docs.length);
  }, [filteredDocuments]);

  return (
    <BusinessCenterOSAdminShell
      title="Document Center"
      description="Organize client folders, project folders, and safe document placeholders without changing storage behavior or touching the existing Memory Media Vault flow."
      statusItems={metrics.slice(0, 4).map(({ label, value }) => ({ label, value }))}
    >
      <section className="py-2">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {metrics.map((metric) => (
              <article key={metric.label} className="business-center-os-card business-center-os-card--light rounded-3xl p-5">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{metric.label}</p>
                <p className="mt-2 text-4xl font-bold text-[#1F2937]">{metric.value}</p>
                <p className="mt-3 text-sm font-semibold leading-7 text-[#475569]">{metric.note}</p>
              </article>
            ))}
          </div>

          <section className="mt-8 grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,.85fr)]">
            <article className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
              <div className="flex flex-col gap-4 border-b border-[rgba(6,182,212,.16)] pb-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Document Workspace</p>
                  <h2 className="mt-1 font-display text-3xl font-bold text-[#F8FAFC]">Document records</h2>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#E2E8F0]">
                    Track document name, type, linked client, linked project, status, and timestamps.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold" onClick={() => setShowCreateForm((current) => !current)}>
                    <Plus className="size-4" aria-hidden="true" />
                    {showCreateForm ? "Hide Create Form" : "New Document"}
                  </button>
                </div>
              </div>

              {showCreateForm ? (
                <form onSubmit={handleSubmit} className="mt-5 grid gap-4 rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4 sm:p-5">
                  <div className="rounded-2xl border border-dashed border-[rgba(6,182,212,.24)] bg-[rgba(6,182,212,.06)] p-4">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-[#22E0D0]">Upload placeholder</p>
                    <p className="mt-2 text-sm font-semibold leading-7 text-[#E2E8F0]">
                      File storage is not wired yet. This slot is reserved for a future safe upload integration.
                    </p>
                    <button
                      type="button"
                      disabled
                      className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(34,224,208,.2)] bg-[rgba(255,255,255,.04)] px-4 text-sm font-extrabold text-[#E2E8F0] opacity-60"
                    >
                      Upload placeholder only
                    </button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Document name" dark>
                      <input value={form.documentName} onChange={(event) => updateForm("documentName", event.target.value)} required className="business-center-os-input focus-ring" placeholder="Discovery summary deck" />
                    </Field>
                    <Field label="Document type" dark>
                      <select value={form.documentType} onChange={(event) => updateForm("documentType", event.target.value as DocumentFormState["documentType"])} className="business-center-os-input focus-ring">
                        {documentTypeOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Folder kind" dark>
                      <select value={form.folderKind} onChange={(event) => updateForm("folderKind", event.target.value as DocumentFormState["folderKind"])} className="business-center-os-input focus-ring">
                        {documentFolderKindOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Status" dark>
                      <select value={form.status} onChange={(event) => updateForm("status", event.target.value as DocumentFormState["status"])} className="business-center-os-input focus-ring">
                        {documentStatusOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Client link" dark>
                      <select value={form.clientId} onChange={(event) => updateForm("clientId", event.target.value)} className="business-center-os-input focus-ring">
                        <option value="">No client linked</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>{client.clientName}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Project link" dark>
                      <select value={form.projectId} onChange={(event) => updateForm("projectId", event.target.value)} className="business-center-os-input focus-ring">
                        <option value="">No project linked</option>
                        {projects.map((project) => (
                          <option key={project.id} value={project.id}>{project.projectName}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Brand scope" dark>
                      <select value={form.brand} onChange={(event) => updateForm("brand", event.target.value as BrandValue)} className="business-center-os-input focus-ring">
                        {brandOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <Field label="Notes" dark>
                    <textarea value={form.notes} onChange={(event) => updateForm("notes", event.target.value)} className="business-center-os-input focus-ring min-h-32" placeholder="Add context about this document and its intended future use." />
                  </Field>

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => resetForm()}
                      className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center justify-center rounded-full px-4 text-sm font-extrabold"
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      disabled={saving || loading || loadingWorkspace}
                      className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                    >
                      {saving ? "Saving..." : "Create Document"}
                    </button>
                  </div>
                </form>
              ) : null}

              <div className="mt-5 flex items-center gap-3">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#22E0D0]" aria-hidden="true" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search document name, type, folder, client, project, status..."
                    className="business-center-os-input focus-ring w-full pl-11"
                  />
                </div>
                <select
                  value={selectedBrand}
                  onChange={(event) => {
                    const nextBrand = event.target.value as BrandValue;
                    setSelectedBrand(nextBrand);
                    resetForm(nextBrand);
                  }}
                  className="business-center-os-input focus-ring min-w-[220px]"
                >
                  {brandOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-5 overflow-hidden rounded-3xl border border-[rgba(6,182,212,.16)]">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[980px] border-collapse">
                    <thead className="bg-[rgba(6,182,212,.1)] text-left text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">
                      <tr>
                        <Th>Document</Th>
                        <Th>Folder / Client / Project</Th>
                        <Th>Type</Th>
                        <Th>Status</Th>
                        <Th>Updated</Th>
                        <Th>Actions</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDocuments.map((document) => (
                        <tr key={document.id} className="border-t border-[rgba(6,182,212,.12)] bg-[rgba(7,26,47,.72)]">
                          <Td>
                            <div className="grid gap-1">
                              <span className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{document.documentNumber}</span>
                              <span className="font-display text-xl font-bold text-[#F8FAFC]">{document.documentName}</span>
                              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[#CBD5E1]">{document.folderKind} folder</span>
                            </div>
                          </Td>
                          <Td>
                            <div className="grid gap-1">
                              <span className="font-bold text-[#F8FAFC]">{document.clientName}</span>
                              <span className="text-xs font-semibold text-[#CBD5E1]">{document.projectName}</span>
                            </div>
                          </Td>
                          <Td>{document.documentType}</Td>
                          <Td>{document.status}</Td>
                          <Td>{formatDate(document.updatedAt)}</Td>
                          <Td>
                            <div className="flex flex-wrap gap-2">
                              <button type="button" className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center rounded-full px-3 text-xs font-extrabold" onClick={() => setSelectedDocumentId(document.id)}>
                                View
                              </button>
                              <button
                                type="button"
                                className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center rounded-full px-3 text-xs font-extrabold"
                                onClick={() => handleDocumentStatusChange(document.id, "Approved")}
                              >
                                Approve
                              </button>
                              <button type="button" className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center rounded-full px-3 text-xs font-extrabold" onClick={() => handleDeleteDocument(document.id)}>
                                Delete
                              </button>
                            </div>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </article>

            <aside className="grid gap-6">
              <section className="business-center-os-card business-center-os-card--light rounded-3xl p-5 sm:p-6">
                <div className="border-b border-[rgba(31,41,55,.12)] pb-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">Document Detail</p>
                  <h3 className="mt-1 font-display text-3xl font-bold text-[#1F2937]">Current document</h3>
                </div>
                {selectedDocument ? (
                  <div className="mt-5 grid gap-4">
                    <div className="rounded-3xl border border-[rgba(20,184,166,.16)] bg-white p-4">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">{selectedDocument.documentNumber}</p>
                      <h4 className="mt-2 font-display text-3xl font-bold text-[#1F2937]">{selectedDocument.documentName}</h4>
                      <p className="mt-2 text-sm font-semibold leading-7 text-[#475569]">{selectedDocument.notes}</p>
                    </div>
                    <InfoRow label="Document type" value={selectedDocument.documentType} />
                    <InfoRow label="Folder kind" value={selectedDocument.folderKind} />
                    <InfoRow label="Client" value={selectedDocument.clientName} />
                    <InfoRow label="Project" value={selectedDocument.projectName} />
                    <InfoRow label="Status" value={selectedDocument.status} />
                    <InfoRow label="Created" value={formatDate(selectedDocument.createdAt)} />
                    <InfoRow label="Updated" value={formatDate(selectedDocument.updatedAt)} />
                    <div className="flex flex-wrap gap-2">
                      {documentStatusOptions.map((status) => (
                        <button key={status} type="button" onClick={() => handleDocumentStatusChange(selectedDocument.id, status)} className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center rounded-full px-3 text-xs font-extrabold">
                          {status}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteDocument(selectedDocument.id)}
                      className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-4 text-sm font-extrabold"
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                      Remove document
                    </button>
                  </div>
                ) : (
                  <div className="mt-5 rounded-3xl border border-dashed border-[rgba(20,184,166,.16)] bg-[rgba(20,184,166,.06)] p-4">
                    <p className="text-sm font-semibold leading-7 text-[#475569]">Select a document to inspect it here.</p>
                  </div>
                )}
              </section>

              <section className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
                <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Client folders</p>
                  <h3 className="mt-1 font-display text-3xl font-bold text-[#F8FAFC]">Folder structure</h3>
                </div>
                <div className="mt-5 grid gap-3">
                  {groupedClientFolders.length ? (
                    groupedClientFolders.map((folder) => (
                      <div key={folder.name} className="rounded-2xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-display text-2xl font-bold text-[#F8FAFC]">{folder.name}</p>
                          <span className="rounded-full border border-[rgba(34,224,208,.22)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">
                            {folder.docs.length}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {folder.docs.slice(0, 3).map((document) => (
                            <span key={document.id} className="rounded-full border border-[rgba(214,178,74,.2)] bg-[rgba(214,178,74,.08)] px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#FF6A2B]">
                              {document.documentName}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm font-semibold leading-7 text-[#E2E8F0]">No client folders found for this brand yet.</p>
                  )}
                </div>
              </section>

              <section className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
                <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Project folders</p>
                  <h3 className="mt-1 font-display text-3xl font-bold text-[#F8FAFC]">Project-linked storage</h3>
                </div>
                <div className="mt-5 grid gap-3">
                  {groupedProjectFolders.length ? (
                    groupedProjectFolders.map((folder) => (
                      <div key={folder.name} className="rounded-2xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-display text-2xl font-bold text-[#F8FAFC]">{folder.name}</p>
                          <span className="rounded-full border border-[rgba(34,224,208,.22)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#22E0D0]">
                            {folder.docs.length}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {folder.docs.slice(0, 3).map((document) => (
                            <span key={document.id} className="rounded-full border border-[rgba(255,255,255,.16)] bg-[rgba(255,255,255,.06)] px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#E2E8F0]">
                              {document.documentName}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm font-semibold leading-7 text-[#E2E8F0]">No project folders found for this brand yet.</p>
                  )}
                </div>
              </section>
            </aside>
          </section>
        </div>
      </section>
    </BusinessCenterOSAdminShell>
  );
}

function useBusinessCenterOSBranding(title: string, description: string) {
  useEffect(() => {
    const fullTitle = `${title} | Business Center OS`;
    document.title = fullTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute("content") || "";
    if (themeMeta) themeMeta.setAttribute("content", "#1F2937");

    const faviconHref = buildBusinessCenterFavicon();
    const iconSelectors = ['link[rel="icon"]', 'link[rel="shortcut icon"]'] as const;
    const icons = iconSelectors.map((selector) => document.querySelector<HTMLLinkElement>(selector)).filter((link): link is HTMLLinkElement => Boolean(link));
    const previousIcons = icons.map((link) => link.getAttribute("href"));
    icons.forEach((link) => {
      link.setAttribute("href", faviconHref);
      link.setAttribute("type", "image/svg+xml");
    });

    return () => {
      if (themeMeta && previousTheme) themeMeta.setAttribute("content", previousTheme);
      icons.forEach((link, index) => {
        const previousHref = previousIcons[index];
        if (previousHref) link.setAttribute("href", previousHref);
      });
    };
  }, [description, title]);
}

function buildBusinessCenterFavicon() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="Business Center OS">
      <defs>
        <linearGradient id="g" x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#FF6B35"/>
          <stop offset=".52" stop-color="#14B8A6"/>
          <stop offset="1" stop-color="#06B6D4"/>
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#1F2937"/>
      <path d="M16 16h18c9 0 16 6.5 16 14.5S43 45 34 45H16z" fill="url(#g)"/>
      <path d="M33 16h15c-4 3.3-6.8 7.6-6.8 13.6 0 6.2 3.2 10.6 7.8 15.4H33c-8.8 0-16-6.4-16-14.5S24.2 16 33 16Z" fill="#F8FAFC" opacity=".1"/>
      <text x="32" y="39" text-anchor="middle" font-size="16" font-family="Arial, Helvetica, sans-serif" font-weight="800" fill="#F8FAFC">BC</text>
    </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(20,184,166,.16)] bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{label}</p>
      <p className="mt-2 break-words text-sm font-bold leading-6 text-[#1F2937]">{value}</p>
    </div>
  );
}

function Field({ label, children, dark = false }: { label: string; children: ReactNode; dark?: boolean }) {
  return (
    <label className={`grid gap-2 text-sm font-extrabold ${dark ? "text-[#F8FAFC]" : "text-[#1F2937]"}`}>
      {label}
      {children}
    </label>
  );
}

function Th({ children }: { children: string }) {
  return <th className="px-4 py-4 align-top">{children}</th>;
}

function Td({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-4 align-top text-sm font-semibold text-[#E2E8F0] ${className}`}>{children}</td>;
}
